import { defineStore } from "pinia";
import { LiveChatMessage } from "~/entities/LiveChatMessage";
import type { ILiveChatRepository } from "~/interfaces/ILiveChatRepository";
import { LiveChatRepository } from "~/repositories/LiveChatRepository";

export const useLiveChatStore = defineStore("LiveChatStore", () => {
  const repository: ILiveChatRepository = new LiveChatRepository();

  const messages = ref<LiveChatMessage[]>();
  const userIdList = ref<string[]>(); // 重複を除いたUserIDのリスト。毎回リストを作成するのは負荷が高そうなのでメッセージが変化した際に取得して保存しておく
  const ownerId = ref<string>("");
  const loading = ref(false);
  const updating = ref(false);

  const prevLiveId = ref<string>();

  const fetch = async (liveId: string) => {
    loading.value = true;
    try {
      checkLiveId(liveId);
      messages.value = await repository.fetch(liveId);
      userIdList.value = getUserIdList(messages.value);
    } finally {
      loading.value = false;
    }
  };

  const insert = async (liveId: string, liveChatMessage: LiveChatMessage): Promise<void> => {
    updating.value = true;
    try {
      await repository.insert(liveId, liveChatMessage);
    } finally {
      updating.value = false;
    }
  };

  const startObserve = (liveId: string) => {
    loading.value = true;
    try {
      checkLiveId(liveId);
      repository.startObserve(liveId, (liveChatMessage: LiveChatMessage) => {
        messages.value?.push(liveChatMessage);
        userIdList.value = getUserIdList(messages.value);
      });
    } finally {
      loading.value = false;
    }
  };

  const stopObserve = () => {
    loading.value = true;
    try {
      repository.stopObserve();
    } finally {
      loading.value = false;
    }
    clearMessage();
  };

  const clearMessage = () => {
    messages.value = [];
    userIdList.value = [];
  };

  const checkLiveId = (liveId: string) => {
    if (liveId !== prevLiveId.value) {
      clearMessage();
    }
    prevLiveId.value = liveId;
  };

  // コメント数
  const messageCount = (chatMessages: LiveChatMessage[]) => {
    if (chatMessages) {
      return chatMessages.filter(item => item.type === "message").length;
    }
    return 0;
  };

  // 来場者数
  const userCountFromUserIdList = (list: string[]) => {
    // 配信者を引いた数を返す
    if (list) {
      if (list.includes(ownerId.value)) {
        return Math.max(0, list.length - 1);
      }
      return Math.max(0, list.length);
    }
    return 0;
  };

  // 重複削除したuserIdを取得する
  const getUserIdList = (chatMessages: LiveChatMessage[] | undefined): string[] => {
    if (chatMessages) {
      const list = chatMessages.map(item => item.userId);
      // 重複削除はSetで行う
      const array = [...new Set(list)];
      return array;
    }
    return [];
  };

  return {
    ownerId,
    messages,
    userIdList,
    loading,
    updating,
    fetch,
    insert, // VisibleForTesting
    startObserve,
    stopObserve,
    messageCount,
    userCountFromUserIdList,
    getUserIdList,
  };
});
